import ProviderSectionContainer, {
  PROVIDER_SECTION_IDS,
} from 'app/ProviderDetails/ProviderSectionContainer'
import styles from './index.module.scss'
import {
  getDisplayBusinessName,
  getPracticeAddressFeatures,
} from 'utils/common'
import PracticeCard from './PracticeCard'
import { LOCATION_URL } from './constants'
import PracticePanelSection from './PracticePanelSection'
import { providerProfileNumber } from 'constants/common'
import logEvent from 'utils/logEvent'
import { RudderEvent } from 'constants/rudderEvents'
import { wpBaseUrl } from './constants'
import { isValidHttpUrl } from 'app/ProviderDetails/utils'
import usePartnershipCodeUrl from 'hooks/usePartnershipCodeUrl'

type PracticeLocationProps = {
  practice: PracticeProvider
  practicesDataForProvider?: ProviderPracticeData
  providerDetails?: Provider
}

const PracticeLocation: React.FC<PracticeLocationProps> = ({
  practice,
  practicesDataForProvider = {},
}) => {
  const addPartnershipCodeToUrl = usePartnershipCodeUrl()
  const {
    streetAddress,
    aptOrSuite,
    city,
    state,
    zipcode,
    googlePlaceId,
  } = getPracticeAddressFeatures(practice)
  const { latitude, longitude, wpBusinessName, wpSlug } = practice ?? {}
  const displayBusinessName = getDisplayBusinessName(wpBusinessName)
  const otherProvidersForPractice = practice.id
    ? practicesDataForProvider[practice.id] ?? []
    : []

  const callPhoneNumberClickedEvent = () => {
    logEvent(RudderEvent.PHONE_NUMBER_CLICKED, {
      phone_number: providerProfileNumber.formattedPhoneNumber,
    })
  }

  const getTitleCasePracticeText = (practiceTitle: string): string =>
    practiceTitle.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase())

  const wpLink = `${wpBaseUrl}/${wpSlug}/` // wordpress link
  const hasWpPracticeLink = wpBusinessName && isValidHttpUrl(wpLink)
  let googleLocationLink = encodeURI(`${LOCATION_URL}${latitude},${longitude}`)

  if (googlePlaceId) {
    googleLocationLink = encodeURI(
      `${LOCATION_URL}${latitude},${longitude}&query_place_id=${googlePlaceId}`
    )
  }

  return (
    <ProviderSectionContainer
      heading="Practice location"
      id={PROVIDER_SECTION_IDS.PRACTICE_LOCATION}
    >
      <div className={styles.practice_location_wrapper}>
        {hasWpPracticeLink ? (
          <div className={styles.practice_location}>
            <a
              href={addPartnershipCodeToUrl(wpLink)}
              target="_blank"
              rel="noreferrer"
              className={styles.practice_location_link}
            >
              {getTitleCasePracticeText(displayBusinessName)}
            </a>
          </div>
        ) : (
          <div className={styles.practice_location}>
            {getTitleCasePracticeText(displayBusinessName)}
          </div>
        )}
        <PracticePanelSection
          containerClass={styles.address_container}
          logoUrl="/icons/practiceLocation2.svg"
          logoAltText="Practice location address"
          title="Address"
          practicePanelLinkHref={googleLocationLink}
          practicePanelLinkTarget="_blank"
          buttonAriaLabel="Open google maps"
        >
          <div>
            {streetAddress && (
              <>
                {streetAddress}
                <br />
              </>
            )}
            {aptOrSuite && (
              <>
                {aptOrSuite}
                <br />
              </>
            )}
            {city}, {state} {zipcode}
          </div>
        </PracticePanelSection>
        <PracticePanelSection
          logoUrl="/icons/mobile-phone.svg"
          logoAltText="Call to book"
          title="Phone number"
          practicePanelLinkHref={`tel:${providerProfileNumber.phoneNumber}`}
          buttonAriaLabel="Call to book appointment"
          handleLinkClick={callPhoneNumberClickedEvent}
        >
          {providerProfileNumber.formattedPhoneNumber}
        </PracticePanelSection>
        {otherProvidersForPractice.length > 0 && (
          <PracticeCard
            practice={practice}
            selected={true}
            otherProvidersForPractice={otherProvidersForPractice}
          />
        )}
      </div>
    </ProviderSectionContainer>
  )
}

export default PracticeLocation
